// Food-log section — the Food log card body.
// Overview view: big calorie ring + three macro rings.
// Detail view: meal cards (meal_type + food items).
// The selected date is driven by the shared <DateNav> header above this card
// (and the Exercise card), so this component no longer owns any date UI.

import { useState } from 'react'
import type { DashboardDay, DashboardTargets } from '../lib/types'
import type { Strings } from '../lib/i18n'

// ── Ring gauge ────────────────────────────────────────────────────────────────

function Ring({
  pct,
  size,
  stroke,
  color,
  label,
  sublabel,
}: {
  pct: number
  size: number
  stroke: number
  color: string
  label: string
  sublabel?: string
}) {
  const r = (size - stroke) / 2
  const circ = 2 * Math.PI * r
  const filled = Math.min(Math.max(pct / 100, 0), 1) * circ
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-label={`${sublabel ?? ''} ${pct}%`}
      role="img"
    >
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--bg-sunk)" strokeWidth={stroke} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={`${filled} ${circ}`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text x={size / 2} y={size / 2 - (sublabel ? 6 : 2)} textAnchor="middle"
        fontSize={size > 80 ? 16 : 10} fontWeight="600" fill="var(--fg)">
        {label}
      </text>
      {sublabel && (
        <text x={size / 2} y={size / 2 + (size > 80 ? 16 : 10)} textAnchor="middle"
          fontSize={size > 80 ? 10 : 8} fill="var(--fg-muted)">
          {sublabel}
        </text>
      )}
    </svg>
  )
}

function OverviewView({ day, targets, s }: { day: DashboardDay; targets: DashboardTargets; s: Strings }) {
  const c = day.consumed
  const t = targets
  return (
    <div className="daylog-overview">
      <div className="daylog-cal-ring">
        <Ring pct={day.pct.calories} size={120} stroke={14} color="var(--brand-primary)"
          label={`${day.pct.calories}%`} sublabel={`${c.calories}/${t.daily_calories}`} />
        <div className="daylog-cal-label">{s.calLabel}</div>
      </div>
      <div className="daylog-macros">
        <div className="daylog-macro">
          <Ring pct={day.pct.protein} size={64} stroke={8} color="#22c55e" label={`${day.pct.protein}%`} />
          <div className="daylog-macro-name">{s.proteinLabel}</div>
          <div className="daylog-macro-val">{c.protein_g}/{t.protein_g}g</div>
        </div>
        <div className="daylog-macro">
          <Ring pct={day.pct.carbs} size={64} stroke={8} color="#f59e0b" label={`${day.pct.carbs}%`} />
          <div className="daylog-macro-name">{s.carbsLabel}</div>
          <div className="daylog-macro-val">{c.carbs_g}/{t.carbs_g}g</div>
        </div>
        <div className="daylog-macro">
          <Ring pct={day.pct.fat} size={64} stroke={8} color="#6366f1" label={`${day.pct.fat}%`} />
          <div className="daylog-macro-name">{s.fatLabel}</div>
          <div className="daylog-macro-val">{c.fat_g}/{t.fat_g}g</div>
        </div>
      </div>
    </div>
  )
}

function DetailView({ day, s }: { day: DashboardDay; s: Strings }) {
  if (day.entries.length === 0) {
    return <p className="daylog-empty">{s.dayNoLog}</p>
  }
  return (
    <div className="daylog-meals">
      {day.entries.map((entry, i) => (
        <div key={i} className="daylog-meal-card">
          <div className="daylog-meal-header">
            <span className="daylog-meal-type">{entry.meal_type}</span>
            <span className="daylog-meal-kcal">{Math.round(entry.calories)} {s.kcalUnit}</span>
          </div>
          {entry.foods.length > 0 && (
            <ul className="daylog-food-list">
              {entry.foods.map((f, j) => (
                <li key={j} className="daylog-food-item">
                  <span className="daylog-food-name">{f.name}</span>
                  <span className="daylog-food-meta">
                    {f.amount_g > 0 ? `${Math.round(f.amount_g)}g · ` : ''}
                    {Math.round(f.calories)} {s.kcalUnit}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  )
}

// ── Main DayLog component ─────────────────────────────────────────────────────

interface Props {
  day: DashboardDay
  targets: DashboardTargets
  strings: Strings
}

type View = 'overview' | 'detail'

export function DayLog({ day, targets, strings: s }: Props) {
  const [view, setView] = useState<View>('overview')

  const viewTabs: Array<{ key: View; label: string }> = [
    { key: 'overview', label: s.tabOverview },
    { key: 'detail', label: s.tabDetail },
  ]

  return (
    <div className="dash-card dash-daylog-card">
      <div className="dash-card-title">{s.daylogTitle}</div>

      {/* View tabs */}
      <div className="dash-tabs">
        {viewTabs.map((v) => (
          <button
            key={v.key}
            type="button"
            className={`dash-tab${view === v.key ? ' dash-tab-active' : ''}`}
            onClick={() => setView(v.key)}
          >
            {v.label}
          </button>
        ))}
      </div>

      {view === 'overview' ? (
        <OverviewView day={day} targets={targets} s={s} />
      ) : (
        <DetailView day={day} s={s} />
      )}
    </div>
  )
}
